import React, { useEffect, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import NumberFormat from "react-number-format";
import { useHistory, useRouteMatch } from "react-router-dom";
import { Button, Dimmer, Loader, Pagination } from "semantic-ui-react";
import OrderModel from '../../admin/components/order/order.model';
import Sidebar from "../../layouts/main/sidebar";
import store from '../../store';
import { formatDateTime, formatMongolianName, parseStatus } from '../../utils/shared.utils';
import './client.orders.scss';
import OrderDetailsComponent from './orders-details.component';

const bucket = process.env.REACT_APP_S3_BUCKET
const region = process.env.REACT_APP_S3_REGION

function mergeCompanyLists(
  a: { id: number | string; name?: string }[],
  b: { id: number | string; name?: string }[]
): { id: number; name: string }[] {
  const m = new Map<number, { id: number; name: string }>();
  for (const x of [...a, ...b]) {
    if (x == null || x.id == null || x.name == null || String(x.name).trim() === "") continue;
    const id = Number(x.id);
    if (Number.isNaN(id)) continue;
    m.set(id, { id, name: String(x.name) });
  }
  return Array.from(m.values()).sort((x, y) => x.name.localeCompare(y.name, "mn"));
}

function CompanyOrdersPage() {
  const history = useHistory();
  const childOrdersRoute = useRouteMatch({ path: "/client/company/child-orders", exact: true });
  const scope = childOrdersRoute ? "children" : "all";
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [fetchedOrders, setFetchedOrders] = useState(false);
  const [page, setPage] = useState({ page: 1, size: 10, totalPages: 1 });
  const [visible, setVisible] = useState(true);
  const [filterCompanyId, setFilterCompanyId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orderedByClientId, setOrderedByClientId] = useState("");
  const [deliveryFromDate, setDeliveryFromDate] = useState("");
  const [deliveryToDate, setDeliveryToDate] = useState("");
  const [orderUsers, setOrderUsers] = useState<{ id: number; parentName?: string; ownName?: string }[]>([]);
  const [filterCompanies, setFilterCompanies] = useState<{ id: number; name: string }[]>([]);
  const [filteredFinalAmount, setFilteredFinalAmount] = useState<number>(0);

  const formatDeliveryDateLabel = (rawDate: any): string => {
    if (!rawDate) return "";
    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getMonth() + 1} сарын ${d.getUTCDate()}`;
  };

  const deliveryDatesSummary = (order: any): string => {
    const dates = (order.deliveries || [])
      .map((d: any) => formatDeliveryDateLabel(d.date))
      .filter(Boolean);
    return Array.from(new Set(dates)).join(", ");
  };

  const toggle = (index: number) => {
    const updatedOrders = [...orders];
    updatedOrders.forEach((o, i) => {
      o.showItems = i === index;
    });
    setOrders(updatedOrders);
    setVisible(false);
  };

  useEffect(() => {
    if (fetchedOrders) {
      return;
    }
    setFetchedOrders(true);
    getCompanyOrders(1);
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("clientToken");
    if (!token || token === "undefined") {
      return;
    }
    let cancelled = false;
    const forScope = scope === "children" ? "children" : "all";
    const headers = {
      Accept: "application/json",
      Authorization: token,
      "Content-Type": "application/json",
    };
    const load = async () => {
      let list: { id: number; name: string }[] = [];
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}client/company/order-filter-companies?forScope=${encodeURIComponent(
            forScope
          )}`,
          { method: "GET", headers }
        );
        if (res.ok) {
          const resJson = (await res.json()) as { companies?: { id: number; name: string }[] };
          list = mergeCompanyLists(resJson.companies || [], []);
        }
      } catch (_) {}
      if (cancelled) return;
      if (list.length === 0) {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_URL}client/company`, { method: "GET", headers });
          if (res.ok) {
            const resJson = (await res.json()) as { company?: { id: number; name: string } };
            const c = resJson.company;
            if (c && c.id != null && c.name) {
              list = mergeCompanyLists([{ id: c.id, name: c.name }], []);
            }
          }
        } catch (_) {}
      }
      if (!cancelled) {
        setFilterCompanies(list);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [scope]);

  useEffect(() => {
    if (!orders?.length) return;
    const fromOrders: { id: number; name: string }[] = [];
    for (const o of orders) {
      if (o.orderingCompanyId != null && o.orderingCompanyName) {
        fromOrders.push({ id: o.orderingCompanyId, name: o.orderingCompanyName });
      }
    }
    if (fromOrders.length === 0) return;
    setFilterCompanies((prev) => mergeCompanyLists(prev, fromOrders));
  }, [orders]);

  useEffect(() => {
    const token = localStorage.getItem("clientToken");
    if (!token || token === "undefined") {
      return;
    }
    fetch(process.env.REACT_APP_API_URL + "client/company/users", {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: token,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) return;
        res.json().then((resJson: { data?: any[] }) => {
          const raw = resJson.data || [];
          const byId = new Map<number, { id: number; parentName?: string; ownName?: string }>();
          for (const u of raw) {
            if (u == null || u.id == null) continue;
            const id = Number(u.id);
            if (Number.isNaN(id)) continue;
            if (!byId.has(id)) {
              byId.set(id, { id, parentName: u.parentName, ownName: u.ownName });
            }
          }
          setOrderUsers(Array.from(byId.values()).sort((a, b) => a.id - b.id));
        });
      })
      .catch(() => {});
  }, []);

  const getCompanyOrders = (
    pageNum: number,
    filterOverrides?: { companyId: string; fromDate: string; toDate: string; deliveryFromDate: string; deliveryToDate: string; clientId: string }
  ) => {
    const token = localStorage.getItem("clientToken");
    if (token && token !== "undefined") {
      setLoading(true);
      fetch(process.env.REACT_APP_API_URL + "client/company/orders", {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          page: { page: pageNum, size: page.size },
          scope,
          filters: filterOverrides || {
            companyId: filterCompanyId,
            fromDate,
            toDate,
            deliveryFromDate,
            deliveryToDate,
            clientId: orderedByClientId,
          },
        }),
      })
        .then((res: any) => {
          setLoading(false);
          if (res.ok) {
            res.json().then((resJson: any) => {
              const nextOrders: OrderModel[] = resJson.data || [];
              for (const order of nextOrders) {
                order.showItems = false;
                order.createdAt = new Date(order.createdAt);
                order.createdAtStr = formatDateTime(order.createdAt);
                order.createdBy = formatMongolianName(order.parentName, order.ownName);
                for (const delivery of order.deliveries || []) {
                  const date = new Date(delivery.date);
                  delivery.date = `${date.getMonth() + 1} сарын ${date.getUTCDate()}`;
                  for (const item of delivery.items || []) {
                    item.product.imageXsUrl = `https://${bucket}.s3.${region}.amazonaws.com/${item.product.imageXsUrl}`;
                  }
                }
              }
              setOrders(nextOrders);
              setPage(resJson.page);
              setFilteredFinalAmount(Number(resJson.filteredFinalAmount || 0));
            });
          }
        })
        .catch((err) => {
          setLoading(false);
          setErrorMsg("Сүлжээний алдаа");
          console.error(err);
        });
    } else {
      store.dispatch({ type: "redirectUrl", data: window.location.pathname });
      history.push("/login");
    }
  };

  const onPageChange = (_: any, data: any) => {
    setPage(Object.assign(page, { page: data.activePage }));
    getCompanyOrders(data.activePage);
    window.scrollTo(0, 100);
  };

  const applyFilters = () => {
    setVisible(true);
    getCompanyOrders(1);
  };

  const resetFilters = () => {
    setFilterCompanyId("");
    setFromDate("");
    setToDate("");
    setDeliveryFromDate("");
    setDeliveryToDate("");
    setOrderedByClientId("");
    setVisible(true);
    getCompanyOrders(1, { companyId: "", fromDate: "", toDate: "", deliveryFromDate: "", deliveryToDate: "", clientId: "" });
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("clientToken");
    if (!token || token === "undefined") return;
    const filters = {
      companyId: filterCompanyId,
      fromDate,
      toDate,
      deliveryFromDate,
      deliveryToDate,
      clientId: orderedByClientId,
    };
    try {
      setLoading(true);
      const res = await fetch(process.env.REACT_APP_API_URL + "client/company/orders", {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          page: { page: 1, size: 1000 },
          scope,
          filters,
        }),
      });
      setLoading(false);
      if (!res.ok) return;
      const json = await res.json();
      const rows: any[] = Array.isArray(json?.data) ? json.data : [];
      const escapeCsv = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      const header = [
        "Захиалгын №",
        "Огноо",
        "Байгууллага",
        "Захиалагч",
        "Хүргэх өдөр",
        "Төлөв",
        "Барааны дүн",
        "Хүргэлтийн дүн",
        "Эцсийн дүн",
      ];
      const csvRows = rows.map((o: any) => {
        const createdAtStr = o.createdAt ? formatDateTime(new Date(o.createdAt)) : "";
        const createdBy = formatMongolianName(o.parentName, o.ownName);
        const deliveryDates = deliveryDatesSummary(o);
        const itemAmount = Number(o.totalAmount || 0) - Number(o.totalDeliveryCost || 0);
        return [
          escapeCsv(o.token),
          escapeCsv(createdAtStr),
          escapeCsv(o.orderingCompanyName || ""),
          escapeCsv(createdBy),
          escapeCsv(deliveryDates),
          escapeCsv(parseStatus(o.status)),
          escapeCsv(itemAmount),
          escapeCsv(Number(o.totalDeliveryCost || 0)),
          escapeCsv(Number(o.totalAmount || 0)),
        ].join(",");
      });
      const totalAmount = Number(json?.filteredFinalAmount || 0);
      const totalRow = [
        escapeCsv("НИЙТ"),
        escapeCsv(""),
        escapeCsv(""),
        escapeCsv(""),
        escapeCsv(""),
        escapeCsv(""),
        escapeCsv(""),
        escapeCsv(""),
        escapeCsv(totalAmount),
      ].join(",");
      const csvContent = [header.map(escapeCsv).join(","), ...csvRows, totalRow].join("\n");
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `company-orders-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const logout = () => {
    localStorage.removeItem("clientToken");
    store.dispatch({ type: "logout" });
    history.push("/login");
  };

  const closeMenu = () => {};

  return (
    <div className="page page-client-orders margin-top-50-sm">
      <div className="client-layout">
        <Dimmer active={loading}>
          <Loader size="medium"></Loader>
        </Dimmer>
        <Sidebar onLogout={logout} closeMenu={closeMenu} />
        <section className="client-orders" id="my-form">
          <div className="client-head">
            {!visible && (
              <IoArrowBack
                size={28}
                onClick={() => {
                  const resetOrders = orders.map((order) => ({
                    ...order,
                    showItems: false,
                  }));
                  setOrders(resetOrders);
                  setVisible(true);
                }}
                color="gray"
              />
            )}
            <h2 style={{ color: visible ? "black" : "gray" }}>
              {scope === "children" ? "Салбарын захиалгууд" : "Байгууллагын захиалгууд"}
            </h2>
          </div>

          {visible ? (
            <div className="company-order-filters">
              <label>
                {scope === "children" ? "Салбар" : "Байгууллага"}
                <select
                  value={filterCompanyId}
                  onChange={(e) => setFilterCompanyId(e.target.value)}
                  aria-label={scope === "children" ? "Салбар" : "Байгууллага"}
                >
                  <option value="">Бүгд</option>
                  {filterCompanies.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Захиалагч
                <select
                  value={orderedByClientId}
                  onChange={(e) => setOrderedByClientId(e.target.value)}
                  aria-label="Захиалагч"
                >
                  <option value="">Бүгд</option>
                  {orderUsers.map((u) => (
                    <option key={u.id} value={String(u.id)}>
                      {formatMongolianName(u.parentName, u.ownName)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Захиалгын огноо
                <input type="date" aria-label="Захиалгын огноо (эхлэх)" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </label>
              <label>
                -
              </label>
              <label>
                <input type="date" aria-label="Захиалгын огноо (дуусах)" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </label>
              <label>
                Хүргэлтийн огноо
                <input type="date" aria-label="Хүргэлтийн огноо (эхлэх)" value={deliveryFromDate} onChange={(e) => setDeliveryFromDate(e.target.value)} />
              </label>
              <label>
                -
              </label>
              <label>
                <input type="date" aria-label="Хүргэлтийн огноо (дуусах)" value={deliveryToDate} onChange={(e) => setDeliveryToDate(e.target.value)} />
              </label>
              <button type="button" onClick={applyFilters}>
                Шүүх
              </button>
              <button type="button" onClick={resetFilters}>
                Цэвэрлэх
              </button>
              <Button type="button" onClick={exportCsv}>
                CSV татах
              </Button>
            </div>
          ) : null}

          {visible ? (
            <div className="company-order-total">
              Эцсийн дүн (шүүлтүүртэй):{" "}
              <NumberFormat
                displayType={"text"}
                value={filteredFinalAmount}
                suffix="₮"
                thousandSeparator={true}
              />
            </div>
          ) : null}

          <div className="errorMsg">{errorMsg}</div>

          {orders?.length > 0 ? (
            <React.Fragment>
              <OrderDetailsComponent
                orders={orders}
                showStaff={true}
                onToggle={toggle}
                visible={visible}
                setVisible={setVisible}
              />

              <div className="flex-div margin-top-50">
                <Pagination
                  defaultActivePage={1}
                  value={page.page}
                  totalPages={page.totalPages}
                  onPageChange={onPageChange}
                ></Pagination>
              </div>
            </React.Fragment>
          ) : (
            <div>{scope === "children" ? "Салбарын захиалга байхгүй байна." : "Танд захиалга байхгүй байна."}</div>
          )}
        </section>
      </div>
    </div>
  );
}

export default CompanyOrdersPage;